import { ReviewEntity } from '@/entities/review.entity';
import { SocketIoEvents } from '@/events/socket-io.events';
import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { logOfferNotAcceptedMarketplacePolicyWarning } from '@/client/logging/offer-not-accepted-policy-logger';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';
import { useEffect, useRef } from 'react';
import type { ClientReviewsSyncDoneEventEntity } from '@/entities/client-reviews-sync-done-event.entity';
import {
    CLIENT_REVIEWS_SYNC_OFFER_NOT_ACCEPTED_SKIP_REASON,
    isClientReviewsSyncQuotaExhaustedSkipReason,
    resolveClientReviewsSyncSkippedMessage,
} from '@/shared/client-reviews-sync-skip-reasons';
import { toast } from '@/ui/toast';
import { createClientSyncQuotaToastId } from '@/ui/toast-ids';

const reviewsPageRealtimeLogger = createClientLogger({
    scope: 'reviews-page-realtime',
});

type UseReviewAnswerJobDoneSyncArgs = {
    events: SocketIoEvents;
    onReviewAnswerJobDone: (review: ReviewEntity) => void;
};

export const useReviewAnswerJobDoneSync = ({ events, onReviewAnswerJobDone }: UseReviewAnswerJobDoneSyncArgs) => {
    useEffect(() => {
        const unsubscribe = events.on('reviewAnswerJobDone', ({ review }) => {
            onReviewAnswerJobDone(review);
        });

        return () => unsubscribe();
    }, [events, onReviewAnswerJobDone]);
};

type UseReviewFeedItemChangedSyncArgs = {
    events: SocketIoEvents;
    onReviewFeedItemChanged: (review: ReviewEntity) => void;
};

export const useReviewFeedItemChangedSync = ({ events, onReviewFeedItemChanged }: UseReviewFeedItemChangedSyncArgs) => {
    useEffect(() => {
        const unsubscribe = events.on('reviewFeedItemChanged', ({ review }) => {
            onReviewFeedItemChanged(review);
        });

        return () => unsubscribe();
    }, [events, onReviewFeedItemChanged]);
};

type UseClientReviewsSyncRefetchArgs = {
    events: SocketIoEvents;
    refetchReviewsAndProducts: (abort?: AbortSignal) => Promise<void>;
};

const createClientReviewsSyncSkipLogContext = (event: ClientReviewsSyncDoneEventEntity) => ({
    action: 'client_reviews_sync.skip',
    clientId: event.clientId,
    platform: event.platform,
    reason: event.reason,
});

const handleSkippedClientReviewsSyncDone = (event: ClientReviewsSyncDoneEventEntity): void => {
    const context = createClientReviewsSyncSkipLogContext(event);

    if (isClientReviewsSyncQuotaExhaustedSkipReason(event.reason)) {
        reviewsPageRealtimeLogger.warn(
            '[client.reviews-page.refetch-client-sync] Client sync skipped because review import quota is exhausted.',
            context,
        );
        toast.warning(resolveClientReviewsSyncSkippedMessage({ reason: event.reason }), {
            autoClose: false,
            toastId: createClientSyncQuotaToastId({
                clientId: event.clientId,
                platform: event.platform,
            }),
        });
        return;
    }

    if (event.reason === CLIENT_REVIEWS_SYNC_OFFER_NOT_ACCEPTED_SKIP_REASON) {
        logOfferNotAcceptedMarketplacePolicyWarning({
            logger: reviewsPageRealtimeLogger,
            message: '[client.reviews-page.refetch-client-sync] Client sync skipped by offer acceptance policy.',
            error: event,
            context,
        });
        return;
    }

    reviewsPageRealtimeLogger.warn(
        '[client.reviews-page.refetch-client-sync] Client sync skipped for an unrecognized reason.',
        context,
    );
};

export const useClientReviewsSyncRefetch = ({ events, refetchReviewsAndProducts }: UseClientReviewsSyncRefetchArgs) => {
    const socketSyncRefetchAbortController = useRef<AbortController | null>(null);

    useEffect(() => {
        const unsubscribe = events.on('clientReviewsSyncDone', async (event) => {
            if (event.skipped) {
                handleSkippedClientReviewsSyncDone(event);
                return;
            }

            reviewsPageRealtimeLogger.debug(
                '[client.reviews-page.refetch-client-sync] Scheduled refetch after sync completion.',
                {
                    clientId: event.clientId,
                    platform: event.platform,
                    skipped: false,
                },
            );
            if (socketSyncRefetchAbortController.current) {
                reviewsPageRealtimeLogger.trace(
                    '[client.reviews-page.refetch-client-sync] Replaced in-flight sync-triggered refetch.',
                    {},
                );
                socketSyncRefetchAbortController.current.abort();
            }

            const abortController = new AbortController();
            socketSyncRefetchAbortController.current = abortController;

            try {
                await refetchReviewsAndProducts(abortController.signal);
            } catch (error) {
                if (isRequestCanceled(error)) {
                    reviewsPageRealtimeLogger.trace(
                        '[client.reviews-page.refetch-client-sync] Sync-triggered refetch was canceled.',
                        {},
                    );
                    return;
                }

                logClientError({
                    logger: reviewsPageRealtimeLogger,
                    message:
                        '[client.reviews-page.refetch-client-sync] Failed to refetch reviews after client sync completion.',
                    error,
                });
            } finally {
                if (socketSyncRefetchAbortController.current === abortController) {
                    socketSyncRefetchAbortController.current = null;
                }
            }
        });

        return () => {
            if (socketSyncRefetchAbortController.current) {
                socketSyncRefetchAbortController.current.abort();
            }
            unsubscribe();
        };
    }, [events, refetchReviewsAndProducts]);
};

type UseRefetchOnReviewsOnPageChangeArgs = {
    reviewsOnPage: number;
    refetchReviewsAndProducts: (abort?: AbortSignal) => Promise<void>;
};

export const useRefetchOnReviewsOnPageChange = ({
    reviewsOnPage,
    refetchReviewsAndProducts,
}: UseRefetchOnReviewsOnPageChangeArgs) => {
    const refetchReviewsAndProductsRef = useRef(refetchReviewsAndProducts);
    const hasMountedRef = useRef(false);

    useEffect(() => {
        refetchReviewsAndProductsRef.current = refetchReviewsAndProducts;
    }, [refetchReviewsAndProducts]);

    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }

        reviewsPageRealtimeLogger.debug(
            '[client.reviews-page.refetch-pagination-update] Scheduled refetch after rows-per-page change.',
            {
                reviewsOnPage,
            },
        );
        const abortController = new AbortController();
        void refetchReviewsAndProductsRef.current(abortController.signal).catch((error) => {
            if (isRequestCanceled(error)) {
                reviewsPageRealtimeLogger.trace(
                    '[client.reviews-page.refetch-pagination-update] Rows-per-page refetch was canceled.',
                    {
                        reviewsOnPage,
                    },
                );
                return;
            }

            logClientError({
                logger: reviewsPageRealtimeLogger,
                message:
                    '[client.reviews-page.refetch-pagination-update] Failed to refetch reviews after rows-per-page update.',
                error,
            });
        });

        return () => abortController.abort();
    }, [reviewsOnPage]);
};

type UseRefetchOnSelectionReconcileRevisionArgs = {
    selectionReconcileRevision: number;
    refetchReviewsAndProducts: (abort?: AbortSignal) => Promise<void>;
};

export const useRefetchOnSelectionReconcileRevision = ({
    selectionReconcileRevision,
    refetchReviewsAndProducts,
}: UseRefetchOnSelectionReconcileRevisionArgs) => {
    const selectionReconcileRefetchAbortController = useRef<AbortController | null>(null);

    useEffect(() => {
        if (selectionReconcileRevision <= 0) {
            return;
        }

        reviewsPageRealtimeLogger.debug(
            '[client.reviews-page.refetch-selection-reconcile] Scheduled refetch after selection reconcile update.',
            {
                selectionReconcileRevision,
            },
        );
        if (selectionReconcileRefetchAbortController.current) {
            reviewsPageRealtimeLogger.trace(
                '[client.reviews-page.refetch-selection-reconcile] Replaced in-flight selection reconcile refetch.',
                {
                    selectionReconcileRevision,
                },
            );
            selectionReconcileRefetchAbortController.current.abort();
        }

        const abortController = new AbortController();
        selectionReconcileRefetchAbortController.current = abortController;

        void refetchReviewsAndProducts(abortController.signal)
            .catch((error) => {
                if (isRequestCanceled(error)) {
                    reviewsPageRealtimeLogger.trace(
                        '[client.reviews-page.refetch-selection-reconcile] Selection reconcile refetch was canceled.',
                        {
                            selectionReconcileRevision,
                        },
                    );
                    return;
                }

                logClientError({
                    logger: reviewsPageRealtimeLogger,
                    message:
                        '[client.reviews-page.refetch-selection-reconcile] Failed to refetch reviews after selection reconcile update.',
                    error,
                });
            })
            .finally(() => {
                if (selectionReconcileRefetchAbortController.current === abortController) {
                    selectionReconcileRefetchAbortController.current = null;
                }
            });

        return () => {
            if (selectionReconcileRefetchAbortController.current === abortController) {
                abortController.abort();
                selectionReconcileRefetchAbortController.current = null;
            }
        };
    }, [selectionReconcileRevision, refetchReviewsAndProducts]);
};
